import { SITE_ID, SITE_NAME } from '@/config/site';
import SuperTokens from 'supertokens-web-js';
import Session from 'supertokens-web-js/recipe/session';
import EmailPassword from 'supertokens-web-js/recipe/emailpassword';
import ThirdParty from 'supertokens-web-js/recipe/thirdparty';
import { getBotFallbackHeaders } from '@/lib/form-token-client';

async function buildExtraAuthHeaders(turnstileToken?: string): Promise<Record<string, string>> {
  const botHeaders = await getBotFallbackHeaders();
  if (turnstileToken) {
    botHeaders['X-Turnstile-Token'] = turnstileToken;
  }
  return botHeaders;
}

const API_DOMAIN = import.meta.env.VITE_API_URL || window.location.origin;

SuperTokens.init({
  appInfo: {
    appName: SITE_NAME,
    apiDomain: API_DOMAIN,
    apiBasePath: '/api/auth',
  },
  // All sites share one SuperTokens instance with multiple OAuth client
  // registrations. clientType tells the backend which Google/Apple/Facebook
  // credentials to use for this site's OAuth flow.
  clientType: SITE_ID,
  recipeList: [
    Session.init({ tokenTransferMethod: "header" }),
    EmailPassword.init(),
    ThirdParty.init(),
  ],
});

// Clear legacy cookies from old domains that cause cross-project session sharing.
// Skip on localhost / single-label hostnames: there's no apex-vs-subdomain
// problem to clean up, and `Domain=.localhost` actively breaks the freshly-set
// SuperTokens cookies (browsers normalize the deletion in a way that knocks
// out the host-only cookie SuperTokens just wrote, leaving E2E with no
// session even after a successful login).
const hostname = typeof window !== 'undefined' ? window.location?.hostname : '';
if (hostname && hostname.includes('.') && hostname !== '127.0.0.1') {
  const cookieDomain = `.${hostname.split('.').slice(-2).join('.')}`;
  ['sAccessToken', 'sRefreshToken', 'sFrontToken', 'st-last-access-token-update'].forEach(name => {
    document.cookie = `${name}=; Domain=.rumio.world; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    document.cookie = `${name}=; Domain=${cookieDomain}; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  });
}

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  /**
   * Effective capabilities, resolved server-side from role + per-user grants
   * (see functions/src/lib/capabilities.ts). Used to decide what the admin UI
   * draws — chiefly whether to offer "Publish to live". Advisory only: the
   * server re-checks every one of these, and must keep doing so.
   */
  capabilities: string[];
  isPlatformAdmin: boolean;
  avatarUrl: string | null;
  hasActiveSubscription: boolean;
  activePlanSlug: string | null;
  /** "View as customer" preview is engaged on this session (admin-only). */
  previewActive: boolean;
  /** Epoch-ms expiry of the active preview, or null. */
  previewExpiresAt: number | null;
}

export async function signIn(email: string, password: string, turnstileToken?: string): Promise<{ user: AuthUser }> {
  const extraHeaders = await buildExtraAuthHeaders(turnstileToken);
  const response = await EmailPassword.signIn({
    formFields: [
      { id: 'email', value: email },
      { id: 'password', value: password },
    ],
    options: {
      preAPIHook: async (context) => ({
        ...context,
        requestInit: {
          ...context.requestInit,
          headers: {
            ...Object.fromEntries(new Headers(context.requestInit.headers as HeadersInit).entries()),
            ...extraHeaders,
          },
        },
      }),
    },
  });

  if (response.status === 'WRONG_CREDENTIALS_ERROR') {
    throw new Error('Invalid email or password');
  }

  if (response.status !== 'OK') {
    throw new Error('Login failed');
  }

  const user = await fetchCurrentUser();
  return { user };
}

export async function signUp(
  email: string,
  password: string,
  name?: string,
  turnstileToken?: string,
  referralCode?: string,
): Promise<{ user: AuthUser }> {
  const extraHeaders = await buildExtraAuthHeaders(turnstileToken);
  // SuperTokens validates that every declared formField is PRESENT in the
  // request, even ones marked `optional: true` — sending too few formFields
  // returns 400 "Are you sending too many / too few formFields?". So we
  // always include `referralCode`, defaulting to an empty string when the
  // signup wasn't routed through a /signup?ref=<code> link.
  const response = await EmailPassword.signUp({
    formFields: [
      { id: 'email', value: email },
      { id: 'password', value: password },
      ...(name ? [{ id: 'name', value: name }] : []),
      { id: 'referralCode', value: referralCode ?? '' },
    ],
    options: {
      preAPIHook: async (context) => ({
        ...context,
        requestInit: {
          ...context.requestInit,
          headers: {
            ...Object.fromEntries(new Headers(context.requestInit.headers as HeadersInit).entries()),
            ...extraHeaders,
          },
        },
      }),
    },
  });

  if (response.status === 'FIELD_ERROR') {
    const error = response.formFields.find((f) => f.error)?.error || 'Validation failed';
    throw new Error(error);
  }

  if (response.status !== 'OK') {
    throw new Error('Registration failed');
  }

  const user = await fetchCurrentUser();
  return { user };
}

export type SocialProviderId = 'google' | 'apple' | 'facebook';

/**
 * Base URL of the platform's OAuth relay.
 *
 * Google, Apple and Meta each have exactly ONE redirect URI registered, and it
 * points here — none of them exposes an API to add a URI per tenant domain, and
 * Google caps a client at 100 of them anyway. The relay reads the tenant's
 * `frontendRedirectURI` out of SuperTokens' `state` and bounces the callback
 * back to this origin, so a new tenant or a domain promotion needs no console
 * edit at all. See functions/src/routes/auth-relay.ts.
 *
 * Point VITE_SOCIAL_RELAY_BASE at your own backend to develop against it — but
 * it must stay DIFFERENT from the page origin. SuperTokens only embeds
 * `frontendRedirectURI` in the state when it differs from
 * `redirectURIOnProviderDashboard`, so pointing the relay at the same origin
 * leaves the relay with nothing to route on and it will reject the callback.
 */
const SOCIAL_RELAY_BASE =
  (import.meta.env.VITE_SOCIAL_RELAY_BASE as string | undefined) || 'https://api.rumio.world';

export async function signInWithProvider(provider: SocialProviderId): Promise<void> {
  const authUrl = await ThirdParty.getAuthorisationURLWithQueryParamsAndSetState({
    thirdPartyId: provider,
    // Where the user lands afterwards. Unchanged — the relay bounces here, so
    // AuthCallback, the guest-cart merge and the companion handoff all still
    // see the same origin they always did.
    frontendRedirectURI: `${window.location.origin}/auth/callback`,
    // What is registered with the provider, and what SuperTokens sends as
    // `redirect_uri` in both the authorize call and the token exchange.
    redirectURIOnProviderDashboard: `${SOCIAL_RELAY_BASE}/api/auth/relay/${provider}`,
  });
  window.location.href = authUrl;
}

export const signInWithGoogle = (): Promise<void> => signInWithProvider('google');
export const signInWithApple = (): Promise<void> => signInWithProvider('apple');
export const signInWithFacebook = (): Promise<void> => signInWithProvider('facebook');

export async function handleAuthCallback(): Promise<{ user: AuthUser }> {
  // If a session already exists, the callback was already processed
  // (e.g. component remounted after first successful signInAndUp).
  // Just fetch the user profile and return — don't re-consume the OAuth code.
  if (await Session.doesSessionExist()) {
    const user = await fetchCurrentUser();
    return { user };
  }

  try {
    const response = await ThirdParty.signInAndUp();
    if (response.status !== 'OK') {
      throw new Error('OAuth callback failed');
    }
  } catch (err) {
    // If signInAndUp fails but a session was created by a prior call
    // (e.g. duplicate call with single-use OAuth code), use that session.
    if (await Session.doesSessionExist()) {
      return { user: await fetchCurrentUser() };
    }
    throw err;
  }

  const user = await fetchCurrentUser();
  return { user };
}

export async function signOut(): Promise<void> {
  await Session.signOut();
}

export async function isAuthenticated(): Promise<boolean> {
  return await Session.doesSessionExist();
}

export async function getAccessToken(): Promise<string | null> {
  if (!(await Session.doesSessionExist())) {
    return null;
  }

  try {
    const token = await Session.getAccessToken();
    return token || null;
  } catch {
    return null;
  }
}

async function fetchCurrentUser(): Promise<AuthUser> {
  const token = await getAccessToken();
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${API_DOMAIN}/api/user/profile`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) throw new Error('Failed to fetch user profile');

  const data = await response.json();
  return {
    id: data.id || data.user_id,
    email: data.email || '',
    firstName: data.first_name || '',
    lastName: data.last_name || '',
    role: data.role || 'user',
    capabilities: Array.isArray(data.capabilities) ? data.capabilities : [],
    isPlatformAdmin: data.is_platform_admin || false,
    avatarUrl: data.avatar_url || null,
    hasActiveSubscription: !!data.has_active_subscription,
    activePlanSlug: data.active_plan_slug ?? null,
    previewActive: !!data.preview?.active,
    previewExpiresAt: data.preview?.expiresAt ?? null,
  };
}

export async function sendPasswordResetEmail(email: string, turnstileToken?: string): Promise<void> {
  const extraHeaders = await buildExtraAuthHeaders(turnstileToken);
  const response = await EmailPassword.sendPasswordResetEmail({
    formFields: [{ id: 'email', value: email }],
    options: {
      preAPIHook: async (context) => ({
        ...context,
        requestInit: {
          ...context.requestInit,
          headers: {
            ...Object.fromEntries(new Headers(context.requestInit.headers as HeadersInit).entries()),
            ...extraHeaders,
          },
        },
      }),
    },
  });

  if (response.status === 'FIELD_ERROR') {
    const error = response.formFields.find((f) => f.error)?.error || 'Validation failed';
    throw new Error(error);
  }

  // Always succeed silently — don't reveal whether the email exists
}

export async function submitNewPassword(newPassword: string): Promise<void> {
  const response = await EmailPassword.submitNewPassword({
    formFields: [{ id: 'password', value: newPassword }],
  });

  if (response.status === 'FIELD_ERROR') {
    const error = response.formFields.find((f) => f.error)?.error || 'Validation failed';
    throw new Error(error);
  }

  if (response.status === 'RESET_PASSWORD_INVALID_TOKEN_ERROR') {
    throw new Error('This reset link has expired or is invalid. Please request a new one.');
  }
}

export default {
  signIn,
  signUp,
  signInWithGoogle,
  signInWithApple,
  signInWithFacebook,
  handleAuthCallback,
  signOut,
  isAuthenticated,
  getAccessToken,
  sendPasswordResetEmail,
  submitNewPassword,
};
